import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { City } from 'country-state-city';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import { db, auth } from '../../config/firebase';
import {doc, setDoc} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { avatars } from './ProfileAvatar';
import '../../css/Questionnaire.css'; // Add CSS file

const Questionnaire = () => {
    const [answers, setAnswers] = useState({
        question1: '', // First Name
        lastName: '',
        userName: '',
        question2: '' // Birthday
    });

    const [country, setCountry] = useState(null);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const options = countryList().getData();
    const [currentUser, setCurrentUser] = useState(null);
    const [countryCode, setCountryCode] = useState('');
    const [selectedAvatar, setSelectedAvatar] = useState(null);

    // Rest of existing useEffect hooks...

    const [avatarIndex, setAvatarIndex] = useState(0);

    const handlePrev = () => {
        setAvatarIndex((prev) => (prev === 0 ? avatars.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setAvatarIndex((prev) => (prev === avatars.length - 1 ? 0 : prev + 1));
    };

    useEffect(() => {
        setSelectedAvatar(avatars[avatarIndex].id);
    }, [avatarIndex]);

    useEffect(() => {
        if (countryCode) {
            const citiesData = City.getCitiesOfCountry(countryCode);
            const cityOptions = citiesData?.map(city => ({
                label: city.name,
                value: city.name
            })) || [];
            setCities(cityOptions);
            setSelectedCity(null);
        } else {
            setCities([]);
            setSelectedCity(null);
        }
    }, [countryCode]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setCurrentUser(user);
            } else {
                console.log('User not authenticated');
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    const handleDateChange = (e) => {
        // Existing date handling logic...
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 8) value = value.slice(0, 8);
        if (value.length > 2) value = value.slice(0, 2) + '/' + value.slice(2);
        if (value.length > 5) value = value.slice(0, 5) + '/' + value.slice(5);
        setAnswers({ ...answers, question2: value });

        if (value.length === 10) {
            const [day, month, year] = value.split('/').map(Number);
            const currentYear = new Date().getFullYear();
            const daysInMonth = new Date(year, month, 0).getDate();

            if (
                day > 31 || month > 12 || year > currentYear ||
                day > daysInMonth || year.toString().length !== 4
            ) {
                setError('Invalid date of birth');
            } else {
                setError('');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (error) return;

        if (!currentUser) {
            setError('User not authenticated');
            return;
        }

        if (!answers.question1 || !answers.lastName || !answers.userName || !answers.question2 || !country || !selectedCity || !selectedAvatar) {
            setError('All fields are required');
            return;
        }

        try {
            // Calculate age
            const [day, month, year] = answers.question2.split('/').map(Number);
            const birthDate = new Date(year, month - 1, day);
            const age = new Date().getFullYear() - birthDate.getFullYear();

            const userData = localStorage.getItem('NewUser');
            const user = userData ? JSON.parse(userData) : {};

            const loginMethod = user.loginMethod ||
                (currentUser.providerData[0]?.providerId === 'google.com' ? 'google' : 'email');

            const userInfoDocRef = doc(db, "UserInformation", currentUser.uid);

            await setDoc(userInfoDocRef, {
                uid: currentUser.uid,
                birthday: answers.question2,
                country: country.label,
                city: selectedCity.value,
                countryCode: countryCode,
                email: currentUser.email,
                age: age.toString(),
                name: answers.question1,
                lastName: answers.lastName,
                userName: answers.userName,
                loginMethod: loginMethod,
                updatedAt: new Date().toISOString(),
                inRelation: false,
                inRelationWith: '',
                member: false,
                avatar: selectedAvatar
            }, { merge: true });

            localStorage.removeItem('NewUser');

            console.log("Document successfully written!");
            navigate('/dashboard');
        } catch (error) {
            console.error('Error saving document: ', error);
            setError('Failed to save user information: ' + error.message);
        }
    };

    return (
        <>
            <div className="questions-bg">
                <div className="cloud cloud-1"></div>
                <div className="cloud cloud-2"></div>
                <div className="cloud cloud-3"></div>
                <div className="cloud cloud-4"></div>
                <div className="cloud cloud-5"></div>
                <div className="cloud cloud-6"></div>
            </div>
            <span className="frazza-text-logo">Frazza</span>
        <div className="questions-card">
            <div className="questions">
                <h2 className="title">Let’s answer some questions</h2>
                <form onSubmit={handleSubmit}>
                    <div className="avatar-selection-container">
                        <h3>Choose your avatar</h3>
                        <div className="avatar-carousel-single">
                            <button
                                type="button"
                                className="avatar-arrow"
                                onClick={handlePrev}
                                aria-label="Previous avatar"
                            >
                                &#8592;
                            </button>
                            <div
                                className={`avatar-item selected`}
                                onClick={() => setSelectedAvatar(avatars[avatarIndex].id)}
                            >
                                <div className="avatar-frame">
                                    <img src={avatars[avatarIndex].src} alt={avatars[avatarIndex].alt || "Avatar"} />
                                </div>
                                {selectedAvatar === avatars[avatarIndex].id && (
                                    <div className="avatar-selected-indicator"></div>
                                )}
                            </div>
                            <button
                                type="button"
                                className="avatar-arrow"
                                onClick={handleNext}
                                aria-label="Next avatar"
                            >
                                &#8594;
                            </button>
                        </div>
                    </div>

                    <div className="input-group">
                        <div className="form-row">
                            <div className="form-field">
                                <label className="questionnaireLabel">First Name</label>
                                <input
                                    type="text"
                                    required
                                    value={answers.question1}
                                    onChange={(e) => setAnswers({ ...answers, question1: e.target.value })}
                                />
                            </div>

                            <div className="form-field">
                                <label className="questionnaireLabel">Last Name</label>
                                <input
                                    type="text"
                                    required
                                    value={answers.lastName}
                                    onChange={(e) => setAnswers({ ...answers, lastName: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="form-field">
                            <label className="questionnaireLabel">Username</label>
                            <input
                                type="text"
                                required
                                value={answers.userName}
                                onChange={(e) => setAnswers({ ...answers, userName: e.target.value })}
                                placeholder="Choose a unique username"
                            />
                        </div>

                        <div className="form-field">
                            <label className="questionnaireLabel">Birthday</label>
                            <input
                                type="text"
                                required
                                placeholder="dd/mm/yyyy"
                                value={answers.question2}
                                onChange={handleDateChange}
                            />
                        </div>

                        <div className="form-field">
                            <label className="questionnaireLabel">Country</label>
                            <Select
                                options={options}
                                value={country}
                                onChange={(selectedOption) => {
                                    setCountry(selectedOption);
                                    setCountryCode(selectedOption?.value);
                                }}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>

                        <div className="form-field">
                            <label className="questionnaireLabel">City</label>
                            <Select
                                options={cities}
                                value={selectedCity}
                                onChange={setSelectedCity}
                                isDisabled={!countryCode}
                                placeholder={countryCode ? "Select city" : "Select a country first"}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>

                        {error && <p className="error">{error}</p>}
                    </div>

                    <button type="submit" className="auth-btn">Continue</button>
                </form>
            </div>
        </div>
        </>
    );
};

export default Questionnaire;